import { Component, ChangeDetectionStrategy, inject, computed, input, output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";

import { Operation } from "src/api-service";
import { CoreCompanyDatePipe } from "src/app/shared/pipe/core-company-date.pipe";
import { selectCurrencies } from "src/app/store/application/data/currency/currencies/currencies.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { QuoteVersionActions } from "src/app/store/pages/quote/version/version.actions";

@Component({
    selector: "app-currency-warning-dialog",
    templateUrl: "./currency-warning-dialog.component.html",
    styleUrls: ["./currency-warning-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonModule,
        TranslateModule,
        CoreCompanyDatePipe,
    ],
})
export class CurrencyWarningDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    public readonly ref = inject(DynamicDialogRef, { optional: true });

    /**
     * True when rendered as a step inside the send-quote stepper. Standalone this dialog is a dead
     * end — both buttons close it and the user has to start over; embedded, resolving the rate
     * advances the flow instead.
     */
    public readonly embedded = input(false);
    public readonly resolved = output<void>();
    private readonly store = inject(Store);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly selectedVersion = toSignal(this.store.select(selectVersion));
    public readonly currencies = toSignal(this.store.select(selectCurrencies), { initialValue: [] });

    public readonly versionCurrencyCode = computed(() =>
        this.selectedVersion()?.currencyCode ?? "SEK"
    );

    public readonly versionCurrencyExchangeRateSnapshot = computed(() =>
        this.selectedVersion()?.currencyExchangeRateSnapshot ?? 1.0
    );

    public readonly versionCurrencyCalculatedDate = computed(() =>
        this.selectedVersion()?.currencySnapshotDate ?? this.currencies().find(x => x.code === "SEK")?.updated
    );

    public readonly latestCurrencyExchangeRate = computed(() =>
        this.currencies().find(x => x.code === this.versionCurrencyCode())?.purchaseExchangeRate ?? 1.0
    );

    public readonly exchangeRateDiffInPercent = computed(() =>
        this.getExchangeRateDiffInPercent(this.latestCurrencyExchangeRate(), this.versionCurrencyExchangeRateSnapshot())
    );

    public readonly totalSalesPriceWithSnapshotExchangeRate = computed(() =>
        this.getTotalSalesPriceInCalculatedCurrency(this.versionCurrencyExchangeRateSnapshot())
    );

    public readonly totalSalesPriceWithTodaysExchangeRate = computed(() =>
        this.getTotalSalesPriceInCalculatedCurrency(this.latestCurrencyExchangeRate())
    );

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public confirmCurrencyChange(): void {
        const version = this.selectedVersion();
        const currencies = this.currencies();
        const currencyCode = this.versionCurrencyCode();

        if (!version) return;

        const selectedCurrency = currencies.find(x => x.code === currencyCode);
        const operations: Operation[] = [
            {
                op: "replace",
                path: "/currencyExchangeRateSnapshot",
                value: selectedCurrency?.purchaseExchangeRate,
            },
            {
                op: "replace",
                path: "/currencySnapshotDate",
                value: selectedCurrency?.updated,
            },
        ];

        this.store.dispatch(
            QuoteVersionActions.update({
                versionId: version.id,
                request: operations,
            }),
        );
        this.finish();
    }

    public closeDialog(): void {
        this.finish();
    }

    /** Advance the stepper when embedded; close when standalone. */
    private finish(): void {
        if (this.embedded()) {
            this.resolved.emit();
            return;
        }
        this.ref?.close();
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    private getTotalSalesPriceInCalculatedCurrency(currencyExchangeRate: number): number {
        const totalSalesPrice = this.selectedVersion()?.summary?.totalSalesPriceExVat ?? 0;
        return this.roundTo(totalSalesPrice / currencyExchangeRate, 2);
    }

    private getExchangeRateDiffInPercent(newValue: number, oldValue: number): number {
        const percentageDiffExact = (1 - newValue / oldValue) * 100;
        return this.roundTo(percentageDiffExact, 2);
    }

    private roundTo(valueToRound: number, decimalPoints: number): number {
        const factor = 10 ** decimalPoints;
        return Math.round(valueToRound * factor) / factor;
    }
}
